"use client";

import React from "react";
import { Search, Bell, User, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/ui/logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/theme-toggle";
import { AppConfig } from "@/config/app-config";

export type AppNotification = {
  id: string;
  title: string;
  description?: string;
  timestamp: string;
  read?: boolean;
};

interface UserInfo {
  name: string;
  email?: string;
  avatar?: string;
  role?: string;
}

export interface HeaderProps {
  title?: string;
  subtitle?: string;
  // 사용자 정보
  user?: UserInfo;
  // 알림 관련
  notifications?: AppNotification[];
  notificationCount?: number;
  onNotificationClick?: (notification: AppNotification) => void;
  onViewAllNotifications?: () => void;
  // 검색 관련
  searchPlaceholder?: string;
  onSearch?: (query: string) => void;
  searchValue?: string;
  // 기능 토글
  showSearch?: boolean;
  showNotifications?: boolean;
  showThemeToggle?: boolean;
  // 이벤트 핸들러
  onUserSettingsClick?: () => void;
  onUserLogout?: () => void;
}

export function Header({
  title = "",
  subtitle,
  user = { name: "관리자", role: "Administrator" },
  notifications = [],
  notificationCount = 0,
  onNotificationClick,
  onViewAllNotifications,
  searchPlaceholder = "검색...",
  onSearch,
  searchValue = "",
  showSearch = true,
  showNotifications = true,
  showThemeToggle = true,
  onUserSettingsClick,
  onUserLogout,
}: HeaderProps) {
  const [searchQuery, setSearchQuery] = React.useState(searchValue);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearch?.(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSearch?.(searchQuery);
    }
  };

  // 기본 알림 데이터 (props로 알림이 제공되지 않은 경우)
  const defaultNotifications: AppNotification[] =
    notifications.length > 0
      ? notifications
      : [
          {
            id: "1",
            title: "새로운 사용자 등록",
            description: "사용자가 시스템에 등록되었습니다",
            timestamp: "2분 전",
          },
          {
            id: "2",
            title: "시스템 업데이트 완료",
            description: "모든 컴포넌트가 성공적으로 업데이트되었습니다",
            timestamp: "10분 전",
          },
          {
            id: "3",
            title: "데이터베이스 백업 완료",
            description: "정기 백업이 성공적으로 완료되었습니다",
            timestamp: "1시간 전",
          },
        ];

  const displayNotificationCount =
    notificationCount > 0 ? notificationCount : defaultNotifications.length;

  return (
    <header className="bg-background px-6 py-3">
      <div className="flex items-center justify-between">
        {/* Left: Title & Breadcrumb */}
        <div className="flex items-center space-x-4">
          <div>
            {title && (
              <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            )}
            {subtitle && (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
        </div>

        {/* Right: Search & Actions */}
        <div className="flex items-center space-x-4">
          {/* Search */}
          {showSearch && AppConfig.features.ui.search && (
            <div className="relative hidden md:block">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder={searchPlaceholder}
                className="pl-10 w-64"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyPress={handleKeyPress}
              />
            </div>
          )}

          {/* Theme Toggle */}
          {showThemeToggle && <ThemeToggle />}

          {/* Notifications */}
          {showNotifications && AppConfig.features.ui.notifications && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="relative">
                  <Bell className="w-4 h-4" />
                  {displayNotificationCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-2 -right-2 w-5 h-5 text-xs flex items-center justify-center p-0"
                    >
                      {displayNotificationCount > 99
                        ? "99+"
                        : displayNotificationCount}
                    </Badge>
                  )}
                  <span className="sr-only">알림</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>
                  알림 ({displayNotificationCount})
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {defaultNotifications.slice(0, 5).map((notification) => (
                  <DropdownMenuItem
                    key={notification.id}
                    className="cursor-pointer"
                    onClick={() => onNotificationClick?.(notification)}
                  >
                    <div className="flex flex-col space-y-1 w-full">
                      <p className="text-sm font-medium">
                        {notification.title}
                      </p>
                      {notification.description && (
                        <p className="text-xs text-muted-foreground">
                          {notification.description}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {notification.timestamp}
                      </p>
                    </div>
                  </DropdownMenuItem>
                ))}
                {defaultNotifications.length > 5 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-center cursor-pointer"
                      onClick={onViewAllNotifications}
                    >
                      모든 알림 보기 ({displayNotificationCount - 5}개 더)
                    </DropdownMenuItem>
                  </>
                )}
                {defaultNotifications.length === 0 && (
                  <DropdownMenuItem disabled>
                    새로운 알림이 없습니다
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* User Menu 제거됨 - LNB로 이동됨 */}
        </div>
      </div>
    </header>
  );
}

// 기본 GNB 설정을 위한 편의 컴포넌트
export function DefaultGNB(
  props: Omit<HeaderProps, "user"> & { userName?: string; userRole?: string }
) {
  const {
    userName = "관리자",
    userRole = "Administrator",
    ...otherProps
  } = props;

  return (
    <Header
      {...otherProps}
      // 사용자 정보는 LNB로 이동됨
      user={undefined}
      onViewAllNotifications={() => {
        console.log("모든 알림 보기");
        // 실제 알림 페이지 이동 로직
      }}
    />
  );
}
